"use client";

import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import { useQueryStates } from "nuqs";
import { activityParsers } from "./shared/parsers";
import { getStableTodayIso } from "@/lib/date";
import { buildQueryString } from "@/lib/utils";

export const useActivityQuery = () => {
  const [q, setQ] = useQueryStates(activityParsers, { history: "replace" });

  const setLocationId = useCallback(
    (value: string) =>
      setQ({ locationId: value.trim() || null, lat: null, lng: null }),
    [setQ],
  );

  const setCoordinates = useCallback(
    (lat: number, lng: number) =>
      setQ({ locationId: null, lat: `${lat}`, lng: `${lng}` }),
    [setQ],
  );

  const setSearch = useCallback(
    (value: string) => setQ({ search: value.trim() || null }),
    [setQ],
  );

  const setDate = useCallback(
    (date?: Date) =>
      setQ({ date: date ? dayjs(date).format("YYYY-MM-DD") : null }),
    [setQ],
  );

  const clearSearch = useCallback(
    () =>
      setQ({
        locationId: null,
        search: null,
        lat: null,
        lng: null,
        date: null,
      }),
    [setQ],
  );

  const setFilters = useCallback(
    (updates: Parameters<typeof setQ>[0]) => {
      if (!updates || typeof updates === "function") {
        setQ(updates);
        return;
      }
      const cleaned = Object.fromEntries(
        Object.entries(updates as Record<string, unknown>).map(
          ([key, value]) => [
            key,
            Array.isArray(value) && value.length === 0 ? null : value,
          ],
        ),
      ) as Parameters<typeof setQ>[0];
      setQ(cleaned);
    },
    [setQ],
  );

  const clearFilters = useCallback(
    () =>
      setQ({
        priceRange: null,
        category: null,
        departureTimes: null,
        language: null,
        options: null,
        duration: null,
        difficulty: null,
        includes: null,
        rating: null,
      }),
    [setQ],
  );

  const defaultToday = getStableTodayIso();

  return {
    locationId: q.locationId ?? "",
    search: q.search ?? "",
    lat: q.lat ?? "",
    lng: q.lng ?? "",
    date: q.date ?? q.start ?? defaultToday,
    priceRange: q.priceRange ?? [],
    category: q.category ?? [],
    departureTimes: q.departureTimes ?? [],
    language: q.language ?? [],
    options: q.options ?? [],
    duration: q.duration ?? [],
    difficulty: q.difficulty ?? [],
    includes: q.includes ?? [],
    rating: q.rating ?? [],
    setLocationId,
    setSearch,
    setCoordinates,
    setDate,
    clearSearch,
    setFilters,
    clearFilters,
  };
};

interface TourAct {
  id: string | number;
  title?: string;
  name?: string;
  provinceId?: string | number;
}

export function useTourHref(tour: TourAct, href?: string): string {
  const { date, lat, lng } = useActivityQuery();

  return useMemo(() => {
    if (href) return href;

    const query = buildQueryString({
      search: tour.title ?? tour.name,
      start: date,
      locationId: tour.provinceId,
      lat: lat && lng ? lat : undefined,
      lng: lat && lng ? lng : undefined,
    });

    return `/tour-act/${tour.id}${query ? `?${query}` : ""}`;
  }, [href, tour.id, tour.title, tour.name, tour.provinceId, date, lat, lng]);
}
